/**
 * Express Storage Adapter — MinIO via `/api/storage/*` (JWT).
 *
 * Substitui o adapter do Supabase, que em produção apontava para
 * `http://localhost:54321` (o build do front não recebe VITE_SUPABASE_URL) e
 * fazia todo upload de foto, anexo e comprovante falhar com "Failed to fetch".
 *
 * Contrato com o servidor (`server/src/routes/storage.ts`):
 * - o arquivo vai em base64 num JSON (`express.json` aceita 10 MB, por isso o
 *   limite de 7 MB brutos abaixo — base64 cresce ~37%);
 * - o servidor prefixa TODO path com o tenant (`{tenantId}/{path}`) na ida e na
 *   volta, então o front guarda no banco o path SEM prefixo — é o que este
 *   adapter devolve em `upload` e o que `createSignedUrl` recebe;
 * - a URL assinada já volta alcançável pelo navegador (`/storage/` do nginx),
 *   ver `server/src/storage/minio-client.ts`.
 */

import { fetchApi } from '@/lib/api/fetch-api';
import type { StorageBucket, StorageProvider, StorageUploadResult } from './types';

/** Tamanho máximo do arquivo bruto; acima disso o corpo JSON estoura os 10 MB do servidor. */
export const STORAGE_MAX_BYTES = 7 * 1024 * 1024;

interface ApiEnvelope<T> {
  data: T | null;
  error: { message: string } | null;
}

type UploadBody = File | Blob | ArrayBuffer | ArrayBufferView;

function mensagem(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

async function bytesDe(body: UploadBody): Promise<Uint8Array> {
  if (body instanceof Blob) {
    // jsdom (testes) não implementa Blob.arrayBuffer; FileReader existe nos dois.
    if (typeof body.arrayBuffer === 'function') return new Uint8Array(await body.arrayBuffer());
    return new Promise((resolve, reject) => {
      const leitor = new FileReader();
      leitor.onload = () => resolve(new Uint8Array(leitor.result as ArrayBuffer));
      leitor.onerror = () => reject(leitor.error ?? new Error('Falha ao ler o arquivo'));
      leitor.readAsArrayBuffer(body);
    });
  }
  if (body instanceof ArrayBuffer) return new Uint8Array(body);
  return new Uint8Array(body.buffer, body.byteOffset, body.byteLength);
}

/** Converte o corpo do upload em base64 sem estourar a pilha em arquivos grandes. */
export async function paraBase64(body: UploadBody): Promise<string> {
  const bytes = await bytesDe(body);
  let binario = '';
  const passo = 0x8000;
  for (let i = 0; i < bytes.length; i += passo) {
    binario += String.fromCharCode(...bytes.subarray(i, i + passo));
  }
  return btoa(binario);
}

class ExpressStorageBucket implements StorageBucket {
  constructor(private readonly bucket: string) {}

  async upload(
    path: string,
    body: UploadBody,
    options?: { contentType?: string; upsert?: boolean },
  ): Promise<StorageUploadResult> {
    try {
      const tamanho = body instanceof Blob ? body.size : body.byteLength;
      if (tamanho > STORAGE_MAX_BYTES) {
        const mb = (tamanho / 1024 / 1024).toFixed(1);
        return { path: null, error: { message: `Arquivo de ${mb} MB excede o limite de 7 MB` } };
      }
      const contentType =
        options?.contentType || (body instanceof Blob && body.type) || 'application/octet-stream';
      const res = await fetchApi<ApiEnvelope<{ path: string; contentType?: string }>>('/api/storage/upload', {
        method: 'POST',
        body: { bucket: this.bucket, path, data: await paraBase64(body), contentType },
      });
      // #1676 R2: o tipo que conta é o do SNIFF DOS BYTES no servidor, não o
      // que o cliente declarou acima — quando o servidor devolve o campo.
      return { path, error: null, contentType: res?.data?.contentType };
    } catch (e) {
      return { path: null, error: { message: mensagem(e) } };
    }
  }

  async createSignedUrl(path: string, expiresIn: number) {
    try {
      const res = await fetchApi<ApiEnvelope<{ signedUrl: string }>>('/api/storage/signed-url', {
        method: 'POST',
        body: { bucket: this.bucket, path, expiresIn },
      });
      if (!res?.data?.signedUrl) {
        return { data: null, error: res?.error ?? { message: 'Servidor não devolveu a URL assinada' } };
      }
      return { data: { signedUrl: res.data.signedUrl }, error: null };
    } catch (e) {
      return { data: null, error: { message: mensagem(e) } };
    }
  }

  async createSignedUrls(paths: string[], expiresIn: number) {
    const itens = await Promise.all(
      paths.map(async (path) => {
        const { data, error } = await this.createSignedUrl(path, expiresIn);
        return { path, signedUrl: data?.signedUrl ?? '', error: error?.message ?? null };
      }),
    );
    return { data: itens, error: null };
  }

  async remove(paths: string[]) {
    for (const path of paths) {
      try {
        await fetchApi<ApiEnvelope<{ success: boolean }>>('/api/storage/delete', {
          method: 'POST',
          body: { bucket: this.bucket, path },
        });
      } catch (e) {
        return { error: { message: mensagem(e) } };
      }
    }
    return { error: null };
  }
}

class ExpressStorageAdapter implements StorageProvider {
  from(bucket: string): StorageBucket {
    return new ExpressStorageBucket(bucket);
  }
}

export const expressStorageAdapter = new ExpressStorageAdapter();
